<template>
    <div class="profile-page">
        <LoadingBar v-if="loading" message="Loading Asset..." />

        <div v-else class="container-xl">

            <!-- Breadcrumb -->
            <nav class="profile-breadcrumb" aria-label="breadcrumb">
                <a href="#" @click.prevent="$router.back()">
                    <i class="fa fa-arrow-left"></i> Assets
                </a>
                <span class="sep">/</span>
                <span class="current">{{ asset.name || 'Asset' }}</span>
            </nav>

            <!-- Header -->
            <div class="profile-header">
                <div class="profile-header-icon" style="background:var(--pp-accent-light);color:var(--pp-accent);">
                    <img v-if="asset.photo" :src="getFileUrl(asset.photo)"
                        style="width:100%;height:100%;object-fit:cover;border-radius:12px;" alt="Asset" />
                    <i v-else class="fa fa-cogs"></i>
                </div>
                <div class="profile-header-body">
                    <h3>{{ asset.name || 'Unnamed Asset' }}</h3>
                    <div class="profile-header-meta">
                        <span><i class="fa fa-hashtag"></i>{{ asset.external_id || 'N/A' }}</span>
                        <span v-if="asset.model"><i class="fa fa-tag"></i>{{ asset.model }}</span>
                        <span v-if="asset.serial_number">
                            <i class="fa fa-barcode"></i>{{ asset.serial_number }}
                        </span>
                    </div>
                </div>
                <div class="profile-actions">
                    <span class="profile-badge" :class="criticalityBadgeClass">
                        <i class="fa fa-exclamation-circle"></i> {{ criticality }}
                    </span>
                    <button type="button" class="action-btn action-btn-back" @click="$router.back()">
                        <i class="fa fa-arrow-left"></i> Back
                    </button>
                    <button type="button" class="action-btn action-btn-primary" @click="editAsset">
                        <i class="fa fa-pencil"></i> Edit
                    </button>
                </div>
            </div>

            <!-- Stat bar -->
            <div class="profile-stats">
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-info">
                        <i class="fa fa-calendar"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Purchase Date</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ formatDate(asset.purchase_date) || 'N/A' }}
                        </div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-accent">
                        <i class="fa fa-cubes"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Materials</div>
                        <div class="profile-stat-value">{{ materials.length }}</div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-warning">
                        <i class="fa fa-truck"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Vendors</div>
                        <div class="profile-stat-value">{{ vendors.length }}</div>
                    </div>
                </div>
                <div class="profile-stat-card">
                    <div class="profile-stat-icon c-success">
                        <i class="fa fa-calendar"></i>
                    </div>
                    <div>
                        <div class="profile-stat-label">Created</div>
                        <div class="profile-stat-value" style="font-size:13px;padding-top:4px;">
                            {{ formatDate(asset.created_at) || 'N/A' }}
                        </div>
                    </div>
                </div>
            </div>

            <!-- Main layout -->
            <div class="row g-3">

                <!-- Left: Asset details -->
                <div class="col-lg-8 d-flex flex-column gap-3">

                    <!-- Core details -->
                    <div class="profile-card">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-info-circle"></i> Asset Details
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-field-grid">
                                <div class="profile-field" v-if="asset.code">
                                    <span class="profile-label">Code</span>
                                    <span class="profile-value" style="font-family:monospace;">
                                        {{ asset.code }}
                                    </span>
                                </div>
                                <div class="profile-field" v-if="asset.model">
                                    <span class="profile-label">Model</span>
                                    <span class="profile-value">{{ asset.model }}</span>
                                </div>
                                <div class="profile-field" v-if="asset.manufacturer">
                                    <span class="profile-label">Manufacturer</span>
                                    <span class="profile-value">{{ asset.manufacturer }}</span>
                                </div>
                                <div class="profile-field" v-if="asset.serial_number">
                                    <span class="profile-label">Serial Number</span>
                                    <span class="profile-value" style="font-family:monospace;">
                                        {{ asset.serial_number }}
                                    </span>
                                </div>
                                <div class="profile-field" v-if="categoryName">
                                    <span class="profile-label">Category</span>
                                    <span class="profile-value">
                                        <span class="profile-badge c-accent">{{ categoryName }}</span>
                                    </span>
                                </div>
                                <div class="profile-field" v-if="locationName">
                                    <span class="profile-label">Location</span>
                                    <span class="profile-value">{{ locationName }}</span>
                                </div>
                                <div class="profile-field" v-if="asset.purchase_date">
                                    <span class="profile-label">Purchase Date</span>
                                    <span class="profile-value v-accent">
                                        {{ formatDate(asset.purchase_date) }}
                                    </span>
                                </div>
                                <div class="profile-field" v-if="asset.created_at">
                                    <span class="profile-label">Created At</span>
                                    <span class="profile-value">{{ formatDate(asset.created_at) }}</span>
                                </div>
                            </div>

                            <!-- Description: full-width field -->
                            <div class="profile-field mt-3" v-if="asset.description"
                                style="grid-column:1/-1;">
                                <span class="profile-label">Description</span>
                                <span class="profile-value" style="line-height:1.6;">
                                    {{ asset.description }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Attachment -->
                    <div class="profile-card" v-if="asset.file">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-paperclip"></i> Document Attachment
                            </div>
                            <span class="profile-count-pill">1 file</span>
                        </div>
                        <div class="profile-card-body">
                            <div class="row g-2">
                                <div class="col-6 col-sm-4 col-md-3">
                                    <div class="profile-attachment">
                                        <div class="profile-attachment-thumb">
                                            <i class="fa fa-file-pdf-o"
                                                style="font-size:24px;color:var(--pp-text-muted);"></i>
                                        </div>
                                        <div class="profile-attachment-meta">
                                            <div class="profile-attachment-name">Asset File</div>
                                            <a :href="getFileUrl(asset.file)" target="_blank"
                                                class="profile-attachment-size"
                                                style="color:var(--pp-accent);text-decoration:none;font-size:11px;">
                                                <i class="fa fa-download"></i> Download
                                            </a>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </div>
                    </div>

                </div>

                <!-- Right: Connections sidebar -->
                <div class="col-lg-4 d-flex flex-column gap-3">

                    <!-- Vendors -->
                    <div class="profile-card" v-if="vendors.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-amber-text);">
                                <i class="fa fa-truck" style="color:var(--pp-amber-text);"></i> Vendors
                            </div>
                            <span class="profile-count-pill">{{ vendors.length }}</span>
                        </div>
                        <div class="profile-card-body">
                            <div class="d-flex flex-column gap-2">
                                <div v-for="(v, i) in vendors" :key="v.id || i"
                                    class="d-flex align-items-center gap-2 profile-field"
                                    style="flex-direction:row;padding:10px 12px;">
                                    <div class="profile-avatar c-warning" style="width:28px;height:28px;font-size:11px;">
                                        {{ vendorName(v).charAt(0).toUpperCase() }}
                                    </div>
                                    <span class="profile-value" style="font-size:13px;">
                                        {{ vendorName(v) }}
                                    </span>
                                </div>
                            </div>
                        </div>
                    </div>

                    <!-- Materials -->
                    <div class="profile-card" v-if="materials.length">
                        <div class="profile-card-header">
                            <div class="profile-section-title" style="color:var(--pp-accent);">
                                <i class="fa fa-cubes" style="color:var(--pp-accent);"></i> Materials
                            </div>
                            <span class="profile-count-pill">{{ materials.length }}</span>
                        </div>
                        <div class="profile-card-body">
                            <div class="d-flex flex-wrap gap-2">
                                <span v-for="(m, i) in materials" :key="m.id || i"
                                    class="profile-badge c-accent">
                                    <i class="fa fa-cube me-1"></i>{{ m.name }}
                                </span>
                            </div>
                        </div>
                    </div>

                    <!-- Empty connections state -->
                    <div class="profile-card"
                        v-if="!vendors.length && !materials.length && !asset.file">
                        <div class="profile-card-header">
                            <div class="profile-section-title">
                                <i class="fa fa-plug"></i> Connections
                            </div>
                        </div>
                        <div class="profile-card-body">
                            <div class="profile-empty">
                                <i class="fa fa-plug"></i>
                                <p>No connections yet</p>
                            </div>
                        </div>
                    </div>

                </div>

            </div>

        </div>
    </div>
     <FormModal ref="editModal" title="Edit Asset" id="edit_asset_form_modal">
        <EditAsset v-if="asset" :assetId="asset.external_id" @updated="onCreated"
          @close="closeCreateModal('editModal')" />
      </FormModal>
</template>

<script>
import { form_api_submit } from '@/utils/FormSubmit'
import LoadingBar from '@/components/utils/LoadingBar.vue'
import EditAsset from '@/views/Asset/EditAsset.vue'
import FormModal from '@/components/utils/FormModal.vue'
export default {
    name: 'AssetProfile',
    components: { LoadingBar,
        EditAsset,
         FormModal,
     },

    data() {
        return {
            asset: {},
            loading: false,
            external_id: this.$route.params.external_id,
        }
    },

    computed: {
        categoryName() {
            return this.asset.get_category?.name || this.asset.asset_category?.name || ''
        },

        locationName() {
            return this.asset.get_location?.name || this.asset.location?.name || ''
        },

        vendors() {
            return (this.asset.get_vendors || []).filter(Boolean)
        },

        materials() {
            return (this.asset.get_materials || []).filter(Boolean)
        },

        criticality() {
            const map = { low: 'Low', medium: 'Medium', high: 'High' }
            return map[this.asset.criticality] || this.asset.criticality || '—'
        },

        criticalityClass() {
            return {
                'pill-low':    this.asset.criticality === 'low',
                'pill-medium': this.asset.criticality === 'medium',
                'pill-high':   this.asset.criticality === 'high',
            }
        },
    },

    mounted() {
        this.fetchProfile()
    },

    methods: {
        formatDate(date) {
            if (!date) return '—'
            try {
                return new Date(date).toLocaleDateString('en-US', {
                    year: 'numeric', month: 'short', day: 'numeric',
                })
            } catch {
                return date
            }
        },

        vendorName(v) {
            const first = v.first_name || ''
            const last  = v.last_name  || ''
            return (first + ' ' + last).trim() || v.get_profile?.company_name || 'Vendor'
        },

        getFileUrl(filePath) {
            if (!filePath) return ''
            if (typeof filePath === 'string' && filePath.startsWith('http')) return filePath
            const base = process.env.VUE_APP_API_URL || 'http://localhost:8000'
            return `${base}/${filePath}`
        },

        async fetchProfile() {
            this.loading = true
            const result = await form_api_submit({
                url: `asset/show/${this.external_id}`,
                method: 'GET',
                data: {},
                show_success_toast: false,
                show_error_toast: true,
                remove_pre_toasts: false,
            })
            if (result.success) {
                this.asset = result.data
            }
            this.loading = false
        },
           editAsset(asset) {
      this.editingAsset = asset
      this.$nextTick(() => {
        this.$refs.editModal.showModal()
      })
    },
    closeCreateModal(ref) {
      this.$refs[ref].closeModal()
    },
    async onCreated() {
      await this.fetchProfile()
    },
    },
}
</script>
